import { Socket as SocketIO } from '@aexae/js_tools';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/react';
import axios from 'axios';

import { enrichRequestProps } from './enrichRequestProps';
import { parseApiCodeFromHost } from './parseApiCodeFromHost';

import type {
  ServerEventHandlers,
  SocketEnv,
  SocketEntry,
  SocketInitResult,
  SocketRawResponse,
  SocketRequestMethod,
  SocketRequestProps
} from '../../types/socket';

// TODO: Phase 2 — replace localStorage reads with injection from SocketProvider/AuthContext.

export type LoginResponse = {
  user?: Record<string, unknown>;
  accessToken?: string;
  apiUrl?: string;
  socketUrl?: string | null;
  [key: string]: unknown;
};

/**
 * Traduction TypeScript 1:1 de Socket.js + Api.js.
 * Classe unique gérant la connexion Socket.IO et les requêtes HTTP-over-socket.
 *
 * L'API publique (propriétés + méthodes) doit correspondre EXACTEMENT à l'original
 * pour la rétrocompatibilité avec les 35+ consommateurs.
 *
 * Phase 2 : séparation en SocketService (connexion) + ApiService (requêtes)
 * une fois le SocketProvider en place.
 */
export class ApiService {
  // --- Socket state (from Socket.js) ---
  socket: SocketIO | null = null;
  socketUrl: string | null;
  env: SocketEnv;
  user: Record<string, unknown> = {};
  apiVersion: string | null = null;

  events: ServerEventHandlers = {
    willBeDisconnect: () => {},
    hasBeenDisconnected: () => {},
    disconnected: () => {},
    reconnected: () => {},
    notification: () => {}
  };

  // --- Api state (from Api.js) ---
  readonly isApp: boolean;
  apiCode: string | null;
  sockets: Record<string, SocketEntry>;
  apiUrl: string | null;
  accessToken: string | null;

  constructor() {
    // --- Socket.js constructor ---
    this.socketUrl = localStorage.getItem('socketUrl');
    const rawEnv = localStorage.getItem('env');
    this.env = rawEnv ? (JSON.parse(rawEnv) as SocketEnv) : { parameters: {}, settings: {}, pagesTexts: {} };

    // --- Api.js constructor ---
    this.isApp = Capacitor.getPlatform() !== 'web';
    this.apiCode = localStorage.getItem('apiCode');
    this.sockets = JSON.parse(localStorage.getItem('sockets') ?? '{}') as Record<string, SocketEntry>;
    this.apiUrl = localStorage.getItem('apiUrl');
    this.accessToken = localStorage.getItem('accessToken');

    this.getSettings();
  }

  // =========================================================================
  // Connection lifecycle (from Socket.js)
  // =========================================================================

  initSocket(apiCode = this.apiCode): Promise<SocketInitResult> {
    return new Promise((resolve, reject) => {
      const result: SocketInitResult = {
        sockets: Object.values(this.sockets),
        socketUrl: this.socketUrl,
        user: this.user,
        env: this.env,
        accessToken: this.accessToken,
        apiUrl: this.apiUrl,
        apiVersion: this.apiVersion,
        isDevicePaired: this.env?.isDevicePaired
      };

      if (!apiCode && this.isApp) {
        return resolve(result);
      }

      if (this.socket && apiCode === this.apiCode && this.socket.io.connected) {
        return resolve(result);
      }

      // Socket exists but is dead (power saving, transport failure, etc.) — clean up
      // before creating a new one to avoid stacking stale listeners and buffered emits.
      if (this.socket && !this.socket.io.connected) {
        this.disconnect();
      }

      const connectionTimeout = setTimeout(() => {
        const error = new Error('Délai de connexion dépassé');
        console.error("Timeout lors de l'initialisation du socket");
        reject(error);
      }, 30000);

      this.getSocketUrl(apiCode)
        .then(async ({ socketUrl, apiUrl }) => {
          this.socket = new SocketIO(socketUrl, {
            reconnectionAttempts: Infinity,
            query: {
              uuid: window.env.uuid,
              apiCode: this.apiCode || apiCode
            },
            auth: { token: this.accessToken }
          });

          let initialConnectionDone = false;

          this.socket.on('connect_error', (err: unknown) => {
            clearTimeout(connectionTimeout);
            console.error({ connectError: err, originalError: err });

            // Après la connexion initiale, la reconnexion est gérée par Socket.IO + SocketEvents.
            if (initialConnectionDone) return;

            // Pas de réseau : on laisse le bandeau hors-ligne fournir le feedback.
            if (!navigator.onLine) {
              return reject(new Error('Connexion au serveur échouée'));
            }

            /**
             * Reject avec `maintenance` pour que AuthContext le catch et navigue
             * via react-router (SPA, pas de reload). L'ancien hard nav
             * `window.location.pathname = '/maintenance'` bouclait sous Capacitor :
             * le reload de la WebView peut atterrir sur '/' (SPA fallback quirks),
             * re-déclenchant le redirect à chaque retry de Socket.IO via
             * `reconnectionAttempts: Infinity`.
             */
            reject(new Error('maintenance'));
          });

          this.socket.on('connect', async () => {
            initialConnectionDone = true;
            try {
              console.log('connected');

              const wsUrl = new URL(socketUrl);
              this.apiCode = wsUrl.hostname.substring(0, wsUrl.hostname.indexOf('.'));

              const response = (await this.request('GET', '/database/version')) as SocketRawResponse & {
                apiUrl?: string;
                version?: string;
              };
              if (!response.success) {
                clearTimeout(connectionTimeout);
                this.disconnect();
                return reject(response);
              }

              apiUrl = response.apiUrl;
              window.env.apiUrl = apiUrl;
              this.apiUrl = apiUrl ?? null;
              this.apiVersion = response.version ?? null;
              this.socketUrl = socketUrl;
              this.sockets[apiCode!] = {
                code: apiCode!,
                apiCode: this.apiCode,
                socketUrl,
                apiUrl: apiUrl ?? ''
              };

              await this.request('GET', '/company/theme').then((themeResponse: SocketRawResponse) => {
                const pagesTexts = (themeResponse.pagesTexts as Record<string, string> | undefined) ?? {};
                const keys = Object.keys(pagesTexts);
                keys.forEach((key) => {
                  if (key === 'login') {
                    pagesTexts[key] = pagesTexts[key].replace(/[\r]/g, '\r\n');
                  }
                });

                this.env = {} as SocketEnv;
                this.env.logo = themeResponse.logo as string | undefined;
                this.env.parameters = themeResponse.parameters as SocketEnv['parameters'];
                this.env.settings = themeResponse.settings as SocketEnv['settings'];
                this.env.settingsMobile = themeResponse.settingsMobile as SocketEnv['settingsMobile'];
                this.env.pagesTexts = pagesTexts;
                this.env.isClockingSiteEnabled = themeResponse.isClockingSiteEnabled as boolean | undefined;
                this.env.isDevicePaired = themeResponse.isDevicePaired as boolean | undefined;
                result.isDevicePaired = themeResponse.isDevicePaired as boolean | undefined;

                Sentry.setTag('client', (this.env.parameters?.client_code as string) ?? '');
                Sentry.setContext('app', {
                  version: process.env.REACT_APP_VERSION,
                  env: process.env.NODE_ENV,
                  socketUrl: this.socketUrl,
                  uuid: window.env.uuid
                });
              });

              result.apiVersion = this.apiVersion;
              result.socketUrl = this.socketUrl;
              result.sockets = Object.values(this.sockets);
              result.env = this.env;
              this.setSettings(apiCode!);

              if (!this.accessToken) {
                clearTimeout(connectionTimeout);
                return resolve(result);
              }

              this.socket!.emit(
                'getUser',
                this.accessToken,
                ({ user = {}, apiUrl }: { user?: Record<string, unknown>; apiUrl?: string }) => {
                  clearTimeout(connectionTimeout);
                  this.user = user;
                  result.user = this.user;
                  if (apiUrl) {
                    this.apiUrl = apiUrl;
                    result.apiUrl = this.apiUrl;
                  }
                  resolve(result);
                }
              );
            } catch (err: unknown) {
              clearTimeout(connectionTimeout);
              console.error('Erreur lors de la connexion:', err);

              const message = err instanceof Error ? err.message : String(err);
              if (message === 'maintenance') {
                if (window.location.pathname.includes('/maintenance')) {
                  return reject(err);
                }
                window.location.pathname = `${window.env.PUBLIC_URL}/maintenance`;
              }
              reject(err);
            }
          });

          this.initListeners();
        })
        .catch((err: unknown) => {
          clearTimeout(connectionTimeout);
          console.error("Erreur lors de la récupération de l'URL du socket:", err);
          this.disconnect();
          reject(err);
        });
    });
  }

  getSocketUrl(apiCode: string | null): Promise<{ socketUrl: string; apiUrl?: string }> {
    return new Promise((resolve, reject) => {
      if (!this.isApp) {
        const port = window.env.SOCKET_PORT;
        const socketUrl = `${window.location.protocol}//${window.location.hostname}${port ? `:${port}` : ''}`;
        return resolve({ socketUrl });
      }

      axios
        .get(`${process.env.REACT_APP_AEXAE_API_URL}/clients/access?code=${apiCode}`, {
          timeout: 10000
        })
        .then(({ data: axiosResult }: { data: { success: boolean; access?: { url: string; api_url?: string } } }) => {
          if (!axiosResult.success) {
            console.error(axiosResult);
            const data = apiCode ? this.sockets[apiCode] : undefined;
            if (data) {
              return resolve(data);
            }
            return reject(axiosResult);
          }

          const { url: socketUrl, api_url: resolvedApiUrl } = axiosResult.access!;
          resolve({ socketUrl, apiUrl: resolvedApiUrl ? `${resolvedApiUrl}/api` : resolvedApiUrl });
        })
        .catch((err: unknown) => {
          console.error(err);
          const data = apiCode ? this.sockets[apiCode] : undefined;
          if (data) {
            return resolve(data);
          }
          if (err instanceof Error) {
            err.message = 'maintenance';
          }
          reject(err);
        });
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.io.disconnect();
      this.socket = null;
    }
  }

  cancelDisconnection(): void {
    if (!this.socket) return;
    this.socket.emit('cancelDisconnection', this.accessToken);
  }

  initListeners(): void {
    this.socket!.on('disconnect', (reason: unknown) => {
      console.debug({ disconnect: reason });
    });

    this.socket!.io.io.on('reconnect', (attempt: unknown) => {
      console.debug({ reconnect: attempt });
      this.events.reconnected(attempt as number);
    });

    this.socket!.io.io.on('reconnect_attempt', (attempt: unknown) => {
      console.debug({ reconnect_attempt: attempt });
      if ((attempt as number) === 3) {
        this.events.disconnected('transport close');
      }
    });

    this.socket!.on('hasBeenDisconnected', (reason: unknown) => {
      console.debug({ hasBeenDisconnected: reason });
      this.events.hasBeenDisconnected();
    });

    this.socket!.on('willBeDisconnect', (timeout: unknown) => {
      console.debug({ willBeDisconnect: timeout });
      this.events.willBeDisconnect(timeout as number);
    });
  }

  disconnected(reason: string): void {
    this.events.disconnected(reason);
  }

  get isConnected(): boolean {
    return this.socket?.io?.connected ?? false;
  }

  reconnect(): void {
    if (!this.socket) return;
    if (this.socket.io.connected) return;
    // Force the Manager to re-establish the connection.
    // Useful when returning from background — the Manager may be paused.
    this.socket.io.io.open();
  }

  on(eventKey: string, callback: (...args: unknown[]) => void): void {
    if (!this.socket) return;

    const evKeys = Object.keys(this.events) as Array<keyof ServerEventHandlers>;

    if (!evKeys.includes(eventKey as keyof ServerEventHandlers)) {
      this.socket.io.removeAllListeners(eventKey);
      this.socket.on(eventKey, callback);
    } else {
      (this.events as Record<string, (...args: unknown[]) => void>)[eventKey] = callback;
    }
  }

  // =========================================================================
  // HTTP-over-socket requests (from Api.js)
  // =========================================================================

  request<T = SocketRawResponse>(
    method: SocketRequestMethod,
    route: string,
    props: SocketRequestProps = {},
    options: { skipAuth?: boolean } = {}
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      try {
        enrichRequestProps(props, {
          apiVersion: this.apiVersion,
          apiCode: this.apiCode,
          accessToken: options.skipAuth ? null : this.accessToken,
          env: this.env,
          isClockingApp: window.env.isClockingApp,
          uuid: window.env.uuid
        });

        if (!this.socket) {
          return this.disconnected('socket is not connected');
        }

        /**
         * Timeout de sécurité — évite un loading infini si le serveur ne répond
         * jamais. Pas de check transport-level `socket.io.connected` : il bascule
         * à false pendant les transitions polling↔websocket (et sur les blips
         * transitoires), ce qui rejetterait des requests qui passeraient sinon
         * via la fallback polling. On fait confiance au buffering interne de
         * Socket.IO + ce timeout.
         */
        const requestTimeout = setTimeout(() => {
          reject({
            success: false,
            code: 'REQUEST_TIMEOUT',
            message: 'Request timeout'
          });
        }, 20000);

        this.socket.emit('request', method, route, props, (socketResult: SocketRawResponse) => {
          clearTimeout(requestTimeout);

          if (socketResult.status === 401) {
            // Token invalide/expiré. L'ancien code faisait un `return` nu : la Promise
            // restait PENDING à vie → le 401 n'atteignait jamais React Query/les `await`
            // → spinner infini sur les pages déjà montées (cause du symptôme prod).
            // On nettoie la session, on redirige vers le login et on REJETTE pour que
            // les consommateurs reçoivent une erreur exploitable.
            this.accessToken = null;
            localStorage.removeItem('accessToken');

            const loginPath = window.env.isClockingApp ? '/pointages' : '/login';
            if (!window.location.pathname.includes(loginPath)) {
              window.location.pathname = `${window.env.PUBLIC_URL}${loginPath}`;
            }
            return reject(
              Object.assign(new Error('Session expirée'), { success: false, status: 401, code: 'UNAUTHENTICATED' })
            );
          }

          if (socketResult.code === 'ECONNREFUSED') {
            if (window.location.pathname.includes('/maintenance')) {
              return reject(socketResult);
            }
            reject(socketResult);
            window.location.pathname = `${window.env.PUBLIC_URL}/maintenance`;
            return;
          }

          if (!socketResult.success) {
            if (socketResult.message === 'maintenance' && !window.location.pathname.includes('/maintenance')) {
              window.location.pathname = `${window.env.PUBLIC_URL}/maintenance`;
            }
            return reject(socketResult);
          }

          resolve(socketResult as T);
        });
      } catch (err) {
        console.error(err);
        reject(err);
      }
    });
  }

  get<T = SocketRawResponse>(route: string, props: SocketRequestProps = {}): Promise<T> {
    return this.request<T>('GET', route, props);
  }

  post<T = SocketRawResponse>(route: string, props: SocketRequestProps = {}): Promise<T> {
    return this.request<T>('POST', route, props);
  }

  put<T = SocketRawResponse>(route: string, props: SocketRequestProps = {}): Promise<T> {
    return this.request<T>('PUT', route, props);
  }

  delete<T = SocketRawResponse>(route: string, props: SocketRequestProps = {}): Promise<T> {
    return this.request<T>('DELETE', route, props);
  }

  // =========================================================================
  // Auth (from Api.js)
  // =========================================================================

  /**
   * Logique commune aux deux modes de connexion.
   *
   * `skipAuth` : le proxy webservice (helpers.js#getProps) injecte automatiquement
   * `userId = user.id` sur toute requête portant un accessToken — sur un login,
   * ça transforme la requête en "trouve l'utilisateur X ET d'id Y(=ancien user)"
   * → 0 résultat. On envoie donc la requête sans token, sans toucher à
   * `this.accessToken` (la session courante reste intacte en cas d'échec).
   *
   * Le webservice (events/request.ts) force `success: true` même quand Laravel
   * répond en erreur : l'absence d'accessToken/user est le seul signal d'échec
   * exploitable → on le convertit en rejet explicite.
   */
  private async authenticate(
    route: string,
    props: SocketRequestProps,
    fallbackError: { code: string; message: string }
  ): Promise<LoginResponse> {
    if (window.env.isClockingApp) {
      props.uuid = window.env.uuid;
    }

    const response = await this.request<LoginResponse>('POST', route, props, { skipAuth: true });

    if (!response.accessToken || !response.user) {
      const code = (response.code as string) || fallbackError.code;
      const message = (response.message as string) || fallbackError.message;
      // On recopie la réponse sur l'erreur : certains flux lisent des champs
      // additionnels dans le catch (ex. hasMultipleUsers/users sur LoginForm).
      throw Object.assign(new Error(message), { ...response, success: false, code, message });
    }

    this.user = response.user;
    this.accessToken = response.accessToken;
    this.apiUrl = response.apiUrl ?? null;

    return { ...response, socketUrl: this.socketUrl };
  }

  async login(email: string, password: string, id: number | null = null): Promise<LoginResponse> {
    const props: SocketRequestProps = {
      email,
      password,
      isMobile: this.isApp ? 1 : 0,
      isClockingApp: window.env.isClockingApp
    };

    if (id) {
      props.userId = id;
    }

    return this.authenticate('/login', props, {
      code: 'WARN_INCORRECT_CREDENTIALS',
      message: 'Identifiants incorrects'
    });
  }

  /**
   * Connexion d'un agent par code de 5 chiffres sur une pointeuse MCE.
   * Le serveur crée une session Passport pour l'utilisateur lié à la ressource.
   * L'endpoint backend est public (cors group).
   */
  loginByCode(code: string): Promise<LoginResponse> {
    const props: SocketRequestProps = {
      password: code,
      isMobile: this.isApp ? 1 : 0,
      isClockingApp: window.env.isClockingApp
    };

    return this.authenticate('/clockings/login-token', props, {
      code: 'WARN_INCORRECT_CODE',
      message: 'Code de connexion incorrect'
    });
  }

  logout(): void {
    this.request('POST', '/logout').catch(console.error);
    this.accessToken = null;
    localStorage.removeItem('accessToken');
    this.apiUrl = null;
    localStorage.removeItem('apiUrl');
    this.user = {};
  }

  removeApiCode(apiCode: string): SocketEntry[] {
    delete this.sockets[apiCode];
    localStorage.setItem('sockets', JSON.stringify(this.sockets));

    if (apiCode === this.apiCode) {
      localStorage.removeItem('apiCode');
    }
    return Object.values(this.sockets);
  }

  // =========================================================================
  // Settings (from Api.js)
  // =========================================================================

  getSettings(): void {
    if (!this.isApp) {
      const apiCode = parseApiCodeFromHost(window.location.host);
      if (apiCode) {
        this.apiCode = apiCode;
      }
    }
  }

  setSettings(apiCode: string): void {
    localStorage.setItem('env', JSON.stringify(this.env));

    if (this.isApp) {
      localStorage.setItem('apiCode', apiCode);
      localStorage.setItem('sockets', JSON.stringify(this.sockets));
      localStorage.setItem('apiUrl', this.apiUrl ?? '');
    }
  }
}
